import { Effect, Option, Schema } from "effect";

export const ConflictCode = /* @__PURE__ */ (() => Schema.Literals([
  "conflict", "replay-divergence", "stale-state", "stale-version", "stale-fence"
]))();
export type ConflictCode = typeof ConflictCode.Type;
export const ConflictDetails = /* @__PURE__ */ (() => Schema.Record(Schema.String, Schema.Union([
  Schema.String, Schema.Number, Schema.Boolean, Schema.Null
])))();
export type ConflictDetails = typeof ConflictDetails.Type;
export const ConflictErrorClassification = /* @__PURE__ */ (() => Schema.Struct({
  code: ConflictCode,
  details: ConflictDetails
}))();
export type ConflictErrorClassification = typeof ConflictErrorClassification.Type;

// Common fields are shared by five independently tagged, encodable failures.
const ConflictFields = /* @__PURE__ */ (() => Schema.Struct({
  code: ConflictCode.pipe(Schema.withConstructorDefault(Effect.succeed("conflict" as const))),
  message: Schema.String,
  status: Schema.tag(409),
  details: ConflictDetails.pipe(Schema.withConstructorDefault(Effect.succeed({})))
}))();
type ConflictProperties = typeof ConflictFields["~type.make.in"];
type ConflictOptions = { code?: ConflictCode; details?: ConflictDetails } & Schema.MakeOptions;

export class ConflictError extends Schema.TaggedError<ConflictError>()("ConflictError", ConflictFields) {
  // Preserve legacy family membership without forcing different tagged schemas
  // to inherit an incompatible literal _tag from one another.
  static [Symbol.hasInstance](value: unknown): boolean {
    return this === ConflictError
      ? isConflictInstance(value)
      : Function.prototype[Symbol.hasInstance].call(this, value);
  }

  constructor(message: string | ConflictProperties, options: ConflictOptions = {}) {
    super(typeof message === "string" ? {
      message, code: options.code ?? "conflict", details: options.details ?? {}
    } : message, options);
    Object.freeze(this.details);
  }

  toJSON(): { error: ConflictCode; message: string; details: ConflictDetails } {
    return { error: this.code, message: this.message, details: this.details };
  }
}

const ReplayFields = {
  details: Schema.Struct({ key: Schema.String, operation: Schema.String, recordedOperation: Schema.String }),
  _tag: Schema.tag("ReplayDivergenceError"),
  code: Schema.tag("replay-divergence")
};
const ReplayData = /* @__PURE__ */ (() => Schema.Struct({ ...ConflictFields.fields, ...ReplayFields }))();
type ReplayProperties = typeof ReplayData["~type.make.in"];
export class ReplayDivergenceError extends Schema.TaggedError<ReplayDivergenceError>()("ReplayDivergenceError", ReplayData) {
  toJSON(): { error: ConflictCode; message: string; details: ConflictDetails } {
    return { error: this.code, message: this.message, details: this.details };
  }

  constructor(key: string, operation: string, recordedOperation: string);
  constructor(properties: ReplayProperties, options?: Schema.MakeOptions);
  constructor(key: string | ReplayProperties, operation?: string | Schema.MakeOptions, recordedOperation?: string) {
    super(typeof key === "string" ? {
      message: "Idempotency key was already used with different input.",
      details: { key, operation: operation as string, recordedOperation: recordedOperation as string }
    } : key, typeof operation === "object" ? operation : undefined);
    Object.freeze(this.details);
  }
}

const StateFields = {
  details: Schema.Struct({ subject: Schema.String, expected: Schema.Union([Schema.String, Schema.Number]), observed: Schema.Union([Schema.String, Schema.Number]) }),
  _tag: Schema.tag("StaleStateError"),
  code: Schema.tag("stale-state")
};
const StateData = /* @__PURE__ */ (() => Schema.Struct({ ...ConflictFields.fields, ...StateFields }))();
type StateProperties = typeof StateData["~type.make.in"];
export class StaleStateError extends Schema.TaggedError<StaleStateError>()("StaleStateError", StateData) {
  toJSON(): { error: ConflictCode; message: string; details: ConflictDetails } {
    return { error: this.code, message: this.message, details: this.details };
  }

  constructor(expected: string | number, observed: string | number, subject?: string);
  constructor(properties: StateProperties, options?: Schema.MakeOptions);
  constructor(expected: string | number | StateProperties, observed?: string | number | Schema.MakeOptions, subject = "state") {
    super(typeof expected === "object" ? expected : {
      message: `Expected ${subject} ${expected}, but observed ${observed}.`,
      details: { subject, expected, observed: observed as string | number }
    }, typeof observed === "object" ? observed : undefined);
    Object.freeze(this.details);
  }
}

const VersionFields = {
  details: Schema.Struct({ expectedVersion: Schema.Number, currentVersion: Schema.Number }),
  _tag: Schema.tag("StaleVersionError"),
  code: Schema.tag("stale-version"),
  expectedVersion: Schema.Number,
  currentVersion: Schema.Number
};
const VersionData = /* @__PURE__ */ (() => Schema.Struct({ ...ConflictFields.fields, ...VersionFields }).check(
  Schema.makeFilter(value => value.details.expectedVersion === value.expectedVersion && value.details.currentVersion === value.currentVersion,
    { expected: "version details consistent with the conflict fields" })
))();
type VersionProperties = typeof VersionData["~type.make.in"];
export class StaleVersionError extends Schema.TaggedError<StaleVersionError>()("StaleVersionError", VersionData) {
  toJSON(): { error: ConflictCode; message: string; details: ConflictDetails } {
    return { error: this.code, message: this.message, details: this.details };
  }

  constructor(expectedVersion: number, currentVersion: number);
  constructor(properties: VersionProperties, options?: Schema.MakeOptions);
  constructor(expectedVersion: number | VersionProperties, currentVersion?: number | Schema.MakeOptions) {
    super(typeof expectedVersion === "object" ? expectedVersion : {
      message: `Expected version ${expectedVersion}, but current version is ${currentVersion}.`,
      details: { expectedVersion, currentVersion: currentVersion as number },
      expectedVersion, currentVersion: currentVersion as number
    }, typeof currentVersion === "object" ? currentVersion : undefined);
    Object.freeze(this.details);
  }
}

const FenceFields = {
  details: Schema.Struct({ providedFence: Schema.Number, currentFence: Schema.Number }),
  _tag: Schema.tag("StaleFenceError"),
  code: Schema.tag("stale-fence"),
  providedFence: Schema.Number,
  currentFence: Schema.Number
};
const FenceData = /* @__PURE__ */ (() => Schema.Struct({ ...ConflictFields.fields, ...FenceFields }).check(
  Schema.makeFilter(value => value.details.providedFence === value.providedFence && value.details.currentFence === value.currentFence,
    { expected: "fence details consistent with the conflict fields" })
))();
type FenceProperties = typeof FenceData["~type.make.in"];
export class StaleFenceError extends Schema.TaggedError<StaleFenceError>()("StaleFenceError", FenceData) {
  toJSON(): { error: ConflictCode; message: string; details: ConflictDetails } {
    return { error: this.code, message: this.message, details: this.details };
  }

  constructor(providedFence: number, currentFence: number);
  constructor(properties: FenceProperties, options?: Schema.MakeOptions);
  constructor(providedFence: number | FenceProperties, currentFence?: number | Schema.MakeOptions) {
    super(typeof providedFence === "object" ? providedFence : {
      message: `Fence ${providedFence} no longer grants mutation authority; current fence is ${currentFence}.`,
      details: { providedFence, currentFence: currentFence as number },
      providedFence, currentFence: currentFence as number
    }, typeof currentFence === "object" ? currentFence : undefined);
    Object.freeze(this.details);
  }
}

/** Complete, discriminated conflict family for transport and typed failures. */
export const ConflictFailure = /* @__PURE__ */ (() => Schema.Union([
  ReplayDivergenceError, StaleStateError, StaleVersionError, StaleFenceError,
  ConflictError
]))();
export type ConflictFailure = typeof ConflictFailure.Type;

// Runtime family membership must retain prototype identity. A decoded wire
// shape is not itself an Error, and using instanceOf(ConflictError) here would
// recurse through the compatibility Symbol.hasInstance above.
const ConflictInstance = /* @__PURE__ */ (() => Schema.Union([
  Schema.declare((value): value is ConflictError =>
    Function.prototype[Symbol.hasInstance].call(ConflictError, value)),
  Schema.instanceOf(ReplayDivergenceError), Schema.instanceOf(StaleStateError),
  Schema.instanceOf(StaleVersionError), Schema.instanceOf(StaleFenceError)
]))();
const isConflictInstance = /* @__PURE__ */ (() => Schema.is(ConflictInstance))();
const isErrorInstance = /* @__PURE__ */ (() => Schema.is(Schema.instanceOf(Error)))();
const EMPTY_CONFLICT_DETAILS = Object.freeze({});
const CONFLICT_CODE_BY_NAME: Readonly<Record<string, ConflictCode>> =
  Object.freeze({
    ConflictError: "conflict",
    ReplayDivergenceError: "replay-divergence",
    StaleStateError: "stale-state",
    StaleVersionError: "stale-version",
    StaleFenceError: "stale-fence"
  });
const knownConflictName = /* @__PURE__ */ (() => Schema.decodeUnknownOption(Schema.Literals([
  "ConflictError", "ReplayDivergenceError", "StaleStateError", "StaleVersionError", "StaleFenceError"
])))();
const isRemote = /* @__PURE__ */ (() => Schema.is(Schema.Literal(true)))();
const REMOTE_CONFLICT_MESSAGES = /* @__PURE__ */ (() => Object.freeze([
  {
    matches: Schema.is(Schema.Literal("Idempotency key was already used with different input.")),
    code: "replay-divergence" as const
  },
  {
    matches: Schema.is(Schema.String.check(Schema.isPattern(/^Expected version \d+, but current version is \d+\.$/))),
    code: "stale-version" as const
  },
  {
    matches: Schema.is(Schema.String.check(Schema.isPattern(/^Fence \d+ no longer grants mutation authority; current fence is \d+\.$/))),
    code: "stale-fence" as const
  },
  {
    matches: Schema.is(Schema.String.check(Schema.isPattern(/^Expected .+, but observed .+\.$/s))),
    code: "stale-state" as const
  }
]))();

/** Recover typed conflicts after Workers RPC strips custom Error properties. */
export function classifyConflictError(
  error: unknown
): ConflictErrorClassification | null {
  if (isConflictInstance(error)) {
    return Object.freeze({ code: error.code, details: error.details });
  }
  if (!isErrorInstance(error)) return null;

  let code = Option.getOrNull(knownConflictName(error.name).pipe(
    Option.map(name => CONFLICT_CODE_BY_NAME[name])
  ));
  const remote = isRemote(Reflect.get(error, "remote"));
  if (!code && remote && error.name === "Error") {
    code = REMOTE_CONFLICT_MESSAGES.find(({ matches }) => matches(error.message))?.code ?? null;
  }
  return code
    ? Object.freeze({ code, details: EMPTY_CONFLICT_DETAILS })
    : null;
}
